
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

const InvitationPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [invitationData, setInvitationData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError("Invalid invitation link");
        setIsLoading(false);
        return;
      }
      
      try {
        // Check if token exists and is valid
        const { data, error } = await supabase
          .from("team_invitations")
          .select("*")
          .eq("token", token)
          .eq("status", "pending")
          .single();
          
        if (error || !data) {
          setError("This invitation is invalid or has expired");
          setIsLoading(false);
          return;
        }
        
        // Check if token is expired (with 1 minute grace period)
        const expiryDate = new Date(data.expires_at);
        const now = new Date();
        if (expiryDate.getTime() < now.getTime()) {
          setError("This invitation has expired. Please request a new invitation.");
          setIsLoading(false);
          return;
        }
        
        setInvitationData(data);
        setEmail(data.email);
        setIsLoading(false);
      } catch (error) {
        setError("An error occurred while validating your invitation");
        setIsLoading(false);
      }
    };
    
    validateToken();
  }, [token]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Sign up the user with proper redirect
      // The handle_new_user trigger will automatically update the invitation status
      const redirectUrl = `${window.location.origin}/agency-login`;
      
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName,
            last_name: lastName,
          },
        },
      });
      
      if (authError) throw authError;
      
      toast({
        title: "Success",
        description: "Your account has been created successfully. You can now log in.",
      });
      
      navigate("/agency-login");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An error occurred while creating your account",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Validating invitation...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-2xl">Invalid Invitation</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground">{error}</p>
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button onClick={() => navigate("/login")}>Return to Login</Button>
          </CardFooter>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary-light via-primary/20 to-background z-0 overflow-hidden">
        <div className="absolute top-[10%] left-[5%] w-24 h-24 rounded-full bg-primary/20 blur-2xl"></div>
        <div className="absolute bottom-[15%] right-[10%] w-32 h-32 rounded-full bg-primary/30 blur-xl"></div>
        <div className="absolute top-[40%] right-[15%] w-40 h-40 rounded-full bg-primary/10 blur-3xl"></div>
      </div>
      
      <Card className="w-full max-w-md z-10 shadow-xl bg-card/95 backdrop-blur">
        <CardHeader className="space-y-1">
          <div className="mx-auto mb-2">
            <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><path d="m12 16 4-4-4-4"/><path d="M8 12h8"/><path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            Join Spotlight Adz
          </CardTitle>
          <p className="text-center text-muted-foreground">
            You've been invited to join as a {invitationData?.role}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                readOnly
                className="bg-muted"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="bg-background/50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="bg-background/50"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Create Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-background/50"
                minLength={6}
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating account..." : "Accept Invitation"}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              By accepting this invitation, you agree to our terms and conditions.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvitationPage;
